/*
 * HOG Motion Reveal — vanilla CSS + IntersectionObserver blur-up / fade-up.
 * -------------------------------------------------------------------------
 * Lite entrance motion (CSS + IO) that mirrors the GSAP blur-up feel WITHOUT
 * shipping GSAP/Lenis/SplitText on mobile and non-editorial desktop.
 *
 * Gating:
 *   - Load-entrance: desktop (+ mobile below-fold). Mobile above-fold stays static.
 *     Visual reading-order stagger (top→down, left→right), 80ms overlapping beats.
 *   - Our Story scroll fade-up: all viewports unless prefers-reduced-motion
 *   - Lite IO card/heading scan: skips when HOG.EDITORIAL_MOTION (GSAP owns cards)
 *
 * Pairs with assets/hog-motion-reveal.css (+ critical-motion-reveal.liquid).
 */
(function () {
  'use strict';

  var REDUCED_QUERY = '(prefers-reduced-motion: reduce)';
  var FADEUP_FAILSAFE_MS = 2000;
  var SCROLL_STAGGER_CAP = 3;

  function shouldRunLiteIOScan() {
    if (window.HOG && window.HOG.EDITORIAL_MOTION) return false;
    return true;
  }

  function shouldRunFadeupMotion() {
    return !window.matchMedia(REDUCED_QUERY).matches;
  }

  function isOurStoryPage() {
    return window.HOG && window.HOG.PAGE_TEMPLATE === 'page.ourstory';
  }

  function isPageTemplate() {
    var t = window.HOG && window.HOG.PAGE_TEMPLATE;
    return typeof t === 'string' && (t === 'page' || t.indexOf('page.') === 0);
  }

  var HEADING_SELECTOR = 'h1, h2';
  var CARD_SELECTOR =
    'product-card, .product-card, .product-card--hog, .card, .card-product, .product-item, ' +
    '.collection-card, .featured-blog-posts-card, .blog-post-card, .hog-image-cards__card';

  var EXCLUDE_SELECTOR =
    '.no-animation, marquee-component, #header-group, header-component, header-actions, ' +
    'cart-drawer-component, .cart-drawer, announcement-bar, announcement-bar-component, ' +
    '.slideshow-section, slideshow-arrows, ' +
    'predictive-search-component, .search-action, ' +
    'signup-popup-component, .signup-popup';

  var CARD_SKIP_ANCESTOR =
    '.hog-video-reels, .video-reel, .video-wrapper, .video-carousel, .hero, .slideshow-section';

  var MEDIA_ENTRANCE_STAGGER = 80;
  var HEADING_LEAD_MS = 400;
  var SCROLL_CHOREOGRAPHY_STAGGER = 120;
  var FADEUP_Y = '24px';
  var READING_ORDER_Y_TOLERANCE = 12;
  var MOBILE_QUERY = '(max-width: 750px)';

  var observer = null;
  var scrollFadeupObserver = null;
  var pageChoreographyObserver = null;
  var pageChoreographyPrepared = typeof WeakMap === 'function' ? new WeakMap() : null;
  var motionPendingCleared = false;

  function clearMotionPending() {
    if (motionPendingCleared) return;
    motionPendingCleared = true;
    document.documentElement.classList.remove('hog-motion-pending', 'hog-motion-pending--gsap-heading');
  }

  function isMobileViewport() {
    return window.matchMedia(MOBILE_QUERY).matches;
  }

  // Visual reading order: top → down, then left → right (same-row L→R).
  function sortReadingOrder(els) {
    return els.slice().sort(function (a, b) {
      var ra = a.getBoundingClientRect();
      var rb = b.getBoundingClientRect();
      var dy = ra.top - rb.top;
      if (Math.abs(dy) > READING_ORDER_Y_TOLERANCE) return dy;
      return ra.left - rb.left;
    });
  }

  // Above-fold / near-viewport: stay natural-visible (no hide / cascade). LCP + anti-FOUC.
  function isAtfOrNearViewport(el) {
    return isAboveFold(el) || isIoNearViewport(el);
  }

  function shouldSkipAtfMotion(el) {
    if (el.closest('.hog-split-reveal')) return false;
    return isAtfOrNearViewport(el);
  }

  function shouldSkipMobileAtfMotion(el) {
    if (el.closest('.hog-split-reveal')) return false;
    return isMobileViewport() && isAtfOrNearViewport(el);
  }

  function skipLoadEntranceIfMobileAtf(section, candidates) {
    if (!shouldSkipMobileAtfMotion(section)) return false;
    clearMotionPending();
    if (candidates) {
      candidates.forEach(function (c) {
        markRevealSkipped(c.el);
      });
    }
    return true;
  }

  function markRevealSkipped(el) {
    el.classList.add('reveal-text-animated', 'hog-reveal-done');
  }

  function isExcluded(el) {
    return (
      el.classList.contains('no-animation') ||
      el.classList.contains('fade-animated') ||
      el.classList.contains('reveal-text-animated') ||
      el.classList.contains('hog-reveal') ||
      Boolean(el.closest(EXCLUDE_SELECTOR))
    );
  }

  function isAboveFold(el) {
    var rect = el.getBoundingClientRect();
    return rect.top < window.innerHeight && rect.bottom > 0;
  }

  function resolveSplitRevealTarget(el) {
    if (!el) return null;
    if (el.matches('h1, h2, p')) return el;
    return el.querySelector(':scope > :is(p, h1, h2, h3, h4, h5, h6)') || el;
  }

  // Matches lite IO rootMargin ('0px 0px -8% 0px') for same-turn reveal checks.
  function isIoNearViewport(el) {
    var rect = el.getBoundingClientRect();
    var vh = window.innerHeight;
    return rect.top < vh * 0.92 && rect.bottom > 0;
  }

  function afterPaint(cb) {
    var ran = false;
    function run() {
      if (ran) return;
      ran = true;
      cb();
    }
    if (typeof window.requestAnimationFrame === 'function') {
      window.requestAnimationFrame(function () {
        window.requestAnimationFrame(run);
      });
    }
    window.setTimeout(run, 120);
  }

  function isCarouselSlideVisible(slide) {
    return !slide || slide.getAttribute('aria-hidden') !== 'true';
  }

  // Opacity + transform only — content remains available to AT. Media nodes
  // opt into visibility:hidden via .hog-reveal--fadeup-media (image anti-flash).
  function snapFadeupHidden(el, options) {
    options = options || {};
    el.style.opacity = '0';
    el.style.transform = 'translate3d(0, ' + FADEUP_Y + ', 0)';
    if (options.media) {
      el.style.visibility = 'hidden';
      el.classList.add('hog-reveal--fadeup-media');
    }
  }

  function clearFadeupSnap(el) {
    el.style.removeProperty('opacity');
    el.style.removeProperty('transform');
    el.style.removeProperty('visibility');
  }

  function markDone(el) {
    el.classList.add('hog-reveal-done');
    clearFadeupSnap(el);
  }

  function reveal(el) {
    if (el.classList.contains('hog-reveal--fadeup') && !el.classList.contains('is-visible')) {
      clearFadeupSnap(el);
      void el.offsetWidth;
    }

    el.classList.add('is-visible');
    var done = false;
    function onEnd(event) {
      if (done) return;
      if (event && event.target !== el) return;
      done = true;
      el.removeEventListener('transitionend', onEnd);
      markDone(el);
    }
    el.addEventListener('transitionend', onEnd);
    window.setTimeout(onEnd, 1700);
  }

  function watchLateImageLoad(mediaEl) {
    mediaEl.querySelectorAll('img').forEach(function (img) {
      if (img.complete) return;
      img.addEventListener(
        'load',
        function () {
          if (!mediaEl.classList.contains('is-visible')) snapFadeupHidden(mediaEl, { media: true });
        },
        { once: true }
      );
    });
  }

  function prepFadeup(el, prepared, orderRef, options) {
    options = options || {};
    el.classList.add('hog-reveal', 'hog-reveal--fadeup');
    if (!document.documentElement.classList.contains('hog-motion-pending')) {
      snapFadeupHidden(el, options);
    }
    prepared.push({ el: el, order: orderRef.value });
    orderRef.value += 1;
  }

  function prepMediaFadeup(el, prepared, orderRef) {
    el.classList.add('hog-reveal', 'hog-reveal--fadeup');
    if (!document.documentElement.classList.contains('hog-motion-pending')) {
      snapFadeupHidden(el, { media: true });
    } else {
      el.classList.add('hog-reveal--fadeup-media');
    }
    watchLateImageLoad(el);
    prepared.push({ el: el, order: orderRef.value });
    orderRef.value += 1;
  }

  function playFadeupCascade(rootEl, prepared, options) {
    options = options || {};
    var staggerMs = options.staggerMs != null ? options.staggerMs : MEDIA_ENTRANCE_STAGGER;
    var baseDelayMs = options.baseDelayMs || 0;

    if (!prepared.length) {
      clearMotionPending();
      return;
    }
    void (rootEl || prepared[0].el).offsetWidth;

    var started = false;
    function startCascade() {
      if (started) return;
      started = true;
      clearMotionPending();
      prepared.forEach(function (item) {
        window.setTimeout(function () {
          reveal(item.el);
        }, baseDelayMs + item.order * staggerMs);
      });
    }

    afterPaint(startCascade);
    // Failsafe: if rAF/paint is starved, never leave nodes stuck at opacity 0.
    window.setTimeout(startCascade, FADEUP_FAILSAFE_MS + baseDelayMs);
  }

  function isHeadingBlock(el) {
    return (
      /^H[12]$/.test(el.tagName) ||
      el.classList.contains('hog-two-column-story__heading') ||
      Boolean(el.querySelector('h1, h2'))
    );
  }

  function headingOwnedByGsap() {
    return Boolean(
      (window.HOG && window.HOG.HEADING_MOTION) || (window.HOG && window.HOG.EDITORIAL_MOTION)
    );
  }

  // Collect load-entrance candidates (content + media), then prep in visual reading
  // order after layout. No hard-coded “image last”; no empty cascade slots for GSAP
  // headings (SplitText runs in parallel).
  function collectLoadEntranceCandidates(contentRoot, mediaEl, gsapOwnsHeading) {
    var candidates = [];

    if (contentRoot) {
      Array.prototype.forEach.call(contentRoot.children, function (child) {
        if (isExcluded(child)) return;
        if (isHeadingBlock(child) && gsapOwnsHeading) return;
        candidates.push({ el: child, media: false });
      });
    }

    if (mediaEl && !isExcluded(mediaEl)) {
      candidates.push({ el: mediaEl, media: true });
    }

    return candidates;
  }

  // Prep fade-up nodes in visual reading order. Does NOT play — caller decides
  // whether to cascade immediately (ATF / mobile below-fold) or on IO (desktop BTF).
  function prepReadingOrderCascade(section, candidates) {
    var sorted = sortReadingOrder(
      candidates.map(function (c) {
        return c.el;
      })
    );
    var mediaSet = new Set(
      candidates
        .filter(function (c) {
          return c.media;
        })
        .map(function (c) {
          return c.el;
        })
    );

    var prepared = [];
    var orderRef = { value: 0 };
    sorted.forEach(function (el) {
      if (mediaSet.has(el)) prepMediaFadeup(el, prepared, orderRef);
      else prepFadeup(el, prepared, orderRef);
    });
    return prepared;
  }

  function runReadingOrderCascade(section, candidates) {
    if (!candidates.length) {
      clearMotionPending();
      return;
    }

    afterPaint(function () {
      if (skipLoadEntranceIfMobileAtf(section, candidates)) return;

      var prepared = prepReadingOrderCascade(section, candidates);
      playFadeupCascade(section, prepared);
    });
  }

  // Heading first, then body beats with scroll-path stagger (not the ATF 80ms load beat).
  // On editorial pages Lenis+GSAP wire via whenScrollMotionReady before manual headings
  // are registered — retry play once so a fast scroll into view still catches the heading.
  function playSectionChoreography(section, prepared) {
    if (!section || section.dataset.hogChoreographyDone === 'true') return;
    section.dataset.hogChoreographyDone = 'true';

    function playHeading() {
      if (window.HOG && typeof window.HOG.playSectionHeadingReveal === 'function') {
        return window.HOG.playSectionHeadingReveal(section) || 0;
      }
      return 0;
    }

    var headed = playHeading();
    if (!headed) {
      window.setTimeout(playHeading, 120);
      window.setTimeout(playHeading, 400);
    }

    if (prepared && prepared.length) {
      playFadeupCascade(section, prepared, {
        baseDelayMs: HEADING_LEAD_MS,
        staggerMs: SCROLL_CHOREOGRAPHY_STAGGER,
      });
    } else {
      clearMotionPending();
    }
  }

  function ensurePageChoreographyObserver() {
    if (pageChoreographyObserver) return pageChoreographyObserver;
    if (!('IntersectionObserver' in window)) return null;

    pageChoreographyObserver = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          var section = entry.target;
          var prepared = pageChoreographyPrepared ? pageChoreographyPrepared.get(section) : null;
          if (pageChoreographyPrepared) pageChoreographyPrepared.delete(section);
          playSectionChoreography(section, prepared || []);
          pageChoreographyObserver.unobserve(section);
        });
      },
      { threshold: 0.15, rootMargin: '0px 0px -8% 0px' }
    );
    return pageChoreographyObserver;
  }

  // Desktop below-fold: prep while off-screen, play on intersect (anti-FOUC).
  // Shared by load-entrance sections and generic .section-content-wrapper blocks.
  function registerChoreographedSection(section, candidates) {
    if (!section || section.dataset.hogChoreographyObserved === 'true') return;
    section.dataset.hogChoreographyObserved = 'true';

    afterPaint(function () {
      if (skipLoadEntranceIfMobileAtf(section, candidates)) {
        section.dataset.hogChoreographyDone = 'true';
        return;
      }

      // Layout shifted into view during paint — play immediately (editor reload / late layout).
      if (!isMobileViewport() && isAboveFold(section)) {
        var atfPrepared = candidates.length ? prepReadingOrderCascade(section, candidates) : [];
        playSectionChoreography(section, atfPrepared);
        return;
      }

      var prepared = candidates.length ? prepReadingOrderCascade(section, candidates) : [];
      if (pageChoreographyPrepared) pageChoreographyPrepared.set(section, prepared);

      var obs = ensurePageChoreographyObserver();
      if (obs) {
        obs.observe(section);
      } else {
        playSectionChoreography(section, prepared);
      }
    });
  }

  // Generic Horizon `section` blocks: leaf text/buttons inside .section-content-wrapper.
  function collectGenericSectionCandidates(sectionEl, gsapOwnsHeading) {
    var wrapper = sectionEl.querySelector('.section-content-wrapper');
    if (!wrapper) return [];

    var candidates = [];

    function walk(el) {
      if (!el || el.nodeType !== 1) return;
      if (isExcluded(el)) return;
      if (isHeadingBlock(el) && gsapOwnsHeading) return;

      if (el.classList.contains('group-block')) {
        var gbc = el.querySelector(':scope > .group-block-content');
        if (gbc) {
          Array.prototype.forEach.call(gbc.children, walk);
        }
        return;
      }

      if (
        el.classList.contains('text-block') ||
        el.classList.contains('button') ||
        el.classList.contains('button-block') ||
        (el.tagName === 'A' && String(el.className).indexOf('button') !== -1) ||
        el.tagName === 'BUTTON'
      ) {
        candidates.push({ el: el, media: false });
      }
    }

    Array.prototype.forEach.call(wrapper.children, walk);
    return candidates;
  }

  function scheduleLoadEntranceCascade(section, candidates) {
    if (!candidates.length && !headingOwnedByGsap()) {
      clearMotionPending();
      return;
    }
    if (skipLoadEntranceIfMobileAtf(section, candidates)) return;

    // Desktop below-fold: unified section choreographer (heading + body on scroll).
    // Strict isAboveFold — not isAtfOrNearViewport (92% near-zone caused off-screen load plays).
    if (!isMobileViewport() && !isAboveFold(section)) {
      registerChoreographedSection(section, candidates);
      return;
    }

    // Desktop ATF or mobile below-fold: immediate load cascade.
    if (!candidates.length) {
      clearMotionPending();
      return;
    }
    runReadingOrderCascade(section, candidates);
  }

  function initMediaWithContentEntrance(section) {
    if (!section || section.dataset.hogMediaEntrance) return;
    if (isExcluded(section)) return;
    section.dataset.hogMediaEntrance = 'true';

    var candidates = collectLoadEntranceCandidates(
      section.querySelector('.group-block-content'),
      section.querySelector('.media-block'),
      headingOwnedByGsap()
    );
    scheduleLoadEntranceCascade(section, candidates);
  }

  function initTwoColumnStoryEntrance(section) {
    if (!section || section.dataset.hogStoryEntrance) return;
    if (isExcluded(section)) return;
    section.dataset.hogStoryEntrance = 'true';

    var candidates = collectLoadEntranceCandidates(
      section.querySelector('.hog-two-column-story__content'),
      section.querySelector('.hog-two-column-story__media'),
      headingOwnedByGsap()
    );
    scheduleLoadEntranceCascade(section, candidates);
  }

  // All page.* templates: generic Horizon section blocks get scroll choreography.
  function registerPageScrollSections(root) {
    if (!isPageTemplate()) return;

    root = root || document;
    root.querySelectorAll('.shopify-section').forEach(function (sectionEl) {
      if (sectionEl.dataset.hogChoreographyObserved === 'true') return;
      if (sectionEl.querySelector('.hero-slider, .hog-hero-pin-target, .slideshow-section')) return;
      // Load-entrance adapters own these via initMediaWithContentEntrance / initTwoColumnStoryEntrance.
      if (sectionEl.querySelector('.media-with-content, .hog-two-column-story')) return;
      // Our Story specialized scroll fade-up owns these.
      if (isOurStoryPage() && sectionEl.querySelector('.multicolumn, .banner, .collapsible-content')) return;
      if (!sectionEl.querySelector('.section-content-wrapper')) return;

      var candidates = collectGenericSectionCandidates(sectionEl, headingOwnedByGsap());

      // Mobile ATF: natural-visible.
      if (shouldSkipMobileAtfMotion(sectionEl)) {
        sectionEl.dataset.hogChoreographyObserved = 'true';
        sectionEl.dataset.hogChoreographyDone = 'true';
        return;
      }

      // Desktop ATF: leave natural-visible (already in view at init — no hide).
      if (!isMobileViewport() && isAboveFold(sectionEl)) {
        sectionEl.dataset.hogChoreographyObserved = 'true';
        sectionEl.dataset.hogChoreographyDone = 'true';
        return;
      }

      registerChoreographedSection(sectionEl, candidates);
    });
  }

  var LOAD_ENTRANCE_ADAPTERS = [
    { selector: '.media-with-content', init: initMediaWithContentEntrance },
    { selector: '.hog-two-column-story', init: initTwoColumnStoryEntrance },
  ];

  function initAllLoadEntrances(root) {
    root = root || document;
    LOAD_ENTRANCE_ADAPTERS.forEach(function (adapter) {
      root.querySelectorAll(adapter.selector).forEach(adapter.init);
    });
  }

  function resetChoreographyFlags(root) {
    if (!root) return;
    root.querySelectorAll('.media-with-content').forEach(function (el) {
      delete el.dataset.hogMediaEntrance;
      delete el.dataset.hogChoreographyObserved;
      delete el.dataset.hogChoreographyDone;
    });
    root.querySelectorAll('.hog-two-column-story').forEach(function (el) {
      delete el.dataset.hogStoryEntrance;
      delete el.dataset.hogChoreographyObserved;
      delete el.dataset.hogChoreographyDone;
    });
    if (root.classList && root.classList.contains('shopify-section')) {
      delete root.dataset.hogChoreographyObserved;
      delete root.dataset.hogChoreographyDone;
      delete root.dataset.hogScrollFadeupObserved;
      delete root.dataset.hogScrollFadeupPrepped;
      delete root.dataset.hogScrollFadeup;
    }
    root.querySelectorAll('.shopify-section').forEach(function (el) {
      delete el.dataset.hogChoreographyObserved;
      delete el.dataset.hogChoreographyDone;
    });
  }

  function columnHasMedia(item) {
    return Boolean(
      item.querySelector(
        'img, video, .multicolumn-media, .multicolumn-card__image, .multicolumn-card__background-media'
      )
    );
  }

  // Prepared nodes keyed by section — prep runs at register (below fold), play on IO.
  var scrollFadeupPrepared = typeof WeakMap === 'function' ? new WeakMap() : null;

  // Our Story scroll: vary by section type so the page does not feel like
  // identical fade-up on every block (brand register anti-fatigue).
  // CRITICAL: prep while off-screen; never hide on intersect (visible→hide FOUC).
  function prepScrollFadeup(sectionEl) {
    if (sectionEl.dataset.hogScrollFadeupPrepped) {
      return scrollFadeupPrepared ? scrollFadeupPrepared.get(sectionEl) || [] : [];
    }
    if (sectionEl.querySelector('.hero-slider, .hog-hero-pin-target')) return [];
    if (sectionEl.querySelector('.hog-two-column-story')) return [];

    var prepared = [];
    var orderRef = { value: 0 };

    function add(el, options) {
      if (!el || isExcluded(el)) return;
      prepFadeup(el, prepared, orderRef, options);
      if (options && options.media) watchLateImageLoad(el);
      else if (el.querySelector('img')) watchLateImageLoad(el);
    }

    var multicolumn = sectionEl.querySelector('.multicolumn');
    if (multicolumn) {
      var title = multicolumn.querySelector('.multicolumn__title');
      var items = Array.prototype.slice.call(multicolumn.querySelectorAll('.multicolumn-list__item'));
      var mediaItems = items.filter(columnHasMedia);

      add(title);

      if (mediaItems.length > 0) {
        // Visual reading order (L→R, T→D), then cap stagger so long grids don't trail.
        mediaItems = sortReadingOrder(mediaItems);
        mediaItems.slice(0, SCROLL_STAGGER_CAP).forEach(function (item) {
          add(item, { media: true });
        });
        // Remaining media columns: reveal with last capped beat (no extra stagger).
        if (mediaItems.length > SCROLL_STAGGER_CAP) {
          var lastOrder = Math.max(0, orderRef.value - 1);
          mediaItems.slice(SCROLL_STAGGER_CAP).forEach(function (item) {
            if (isExcluded(item)) return;
            item.classList.add('hog-reveal', 'hog-reveal--fadeup');
            snapFadeupHidden(item, { media: true });
            watchLateImageLoad(item);
            prepared.push({ el: item, order: lastOrder });
          });
        }
      }
      // Text-only multicolumn: title beat only (columns ride with the title).
    } else {
      var banner = sectionEl.querySelector('.banner');
      if (banner) {
        // Single beat for hero banner.
        add(banner, { media: true });
      } else {
        var collapsible = sectionEl.querySelector('.collapsible-content');
        if (collapsible) {
          // FAQ: heading only — avoid 7-row stagger chains.
          add(collapsible.querySelector('.collapsible-content__heading'));
        }
      }
    }

    sectionEl.dataset.hogScrollFadeupPrepped = 'true';
    if (scrollFadeupPrepared) scrollFadeupPrepared.set(sectionEl, prepared);
    return prepared;
  }

  function playScrollFadeup(sectionEl) {
    if (sectionEl.dataset.hogScrollFadeup === 'done') return;

    var prepared = scrollFadeupPrepared ? scrollFadeupPrepared.get(sectionEl) : null;
    if (!prepared || prepared.length === 0) return;

    sectionEl.dataset.hogScrollFadeup = 'done';
    playFadeupCascade(sectionEl, prepared);
  }

  function registerOurStoryScrollSections(root) {
    if (!isOurStoryPage()) return;
    if (!scrollFadeupObserver) return;

    root = root || document;
    root.querySelectorAll('.shopify-section').forEach(function (sectionEl) {
      if (sectionEl.dataset.hogScrollFadeupObserved) return;
      if (sectionEl.querySelector('.hero-slider, .hog-hero-pin-target')) return;
      if (sectionEl.querySelector('.hog-two-column-story')) return;
      if (!sectionEl.querySelector('.multicolumn, .banner, .collapsible-content')) return;

      sectionEl.dataset.hogScrollFadeupObserved = 'true';

      // Mobile ATF: natural-visible. Desktop ATF: prep + play load cascade.
      if (isAtfOrNearViewport(sectionEl)) {
        if (isMobileViewport()) {
          sectionEl.dataset.hogScrollFadeup = 'done';
          return;
        }
        var atfPrepared = prepScrollFadeup(sectionEl);
        if (atfPrepared.length) {
          playScrollFadeup(sectionEl);
        } else {
          sectionEl.dataset.hogScrollFadeup = 'done';
        }
        return;
      }

      // Hide while still below fold, then observe — play only on intersect.
      var prepared = prepScrollFadeup(sectionEl);
      if (!prepared.length) {
        sectionEl.dataset.hogScrollFadeup = 'done';
        return;
      }
      scrollFadeupObserver.observe(sectionEl);
    });
  }

  function initOurStoryScrollFadeup() {
    if (!isOurStoryPage()) return;
    if (!('IntersectionObserver' in window)) return;

    scrollFadeupObserver = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          playScrollFadeup(entry.target);
          scrollFadeupObserver.unobserve(entry.target);
        });
      },
      { threshold: 0.15, rootMargin: '0px 0px -8% 0px' }
    );

    registerOurStoryScrollSections(document);
  }

  function prepare(el, variant, index) {
    if (isExcluded(el)) return false;
    if (variant === 'card' && el.closest(CARD_SKIP_ANCESTOR)) return false;
    if (!el.textContent && variant === 'heading') return false;

    // Carousel cards are prepared via prepareCarouselGroup — never per-card here.
    if (variant === 'card' && el.closest('.resource-list__carousel')) return false;

    if (shouldSkipAtfMotion(el)) {
      markRevealSkipped(el);
      return false;
    }

    el.classList.add('hog-reveal', variant === 'heading' ? 'hog-reveal--heading' : 'hog-reveal--card');
    if (index) el.style.setProperty('--hog-reveal-index', String(index));
    return true;
  }

  function headingMotionOwnsHeadings() {
    return Boolean(window.HOG && window.HOG.HEADING_MOTION);
  }

  // Reveal a carousel's prepared cards together in visual L→R order so a peeking
  // (half-off) slide cannot race ahead of the leftmost card via per-card IO.
  var carouselPending =
    typeof WeakMap === 'function' ? new WeakMap() : null;

  function flushCarouselReveal(carousel) {
    var cards = carouselPending ? carouselPending.get(carousel) : null;
    if (!cards || !cards.length) return;
    if (carouselPending) carouselPending.delete(carousel);

    sortReadingOrder(cards).forEach(function (card, i) {
      if (card.classList.contains('is-visible') || card.classList.contains('hog-reveal-done')) return;
      var index = Math.min(i, 8);
      if (index) card.style.setProperty('--hog-reveal-index', String(index));
      else card.style.removeProperty('--hog-reveal-index');
      reveal(card);
    });
  }

  function prepareCarouselGroup(carousel) {
    if (!carousel || !observer) return;

    // ATF/near-viewport: leave natural-visible — prep-after-paint causes FOUC.
    if (isAtfOrNearViewport(carousel)) {
      carousel.querySelectorAll(CARD_SELECTOR).forEach(function (card) {
        if (isExcluded(card) || card.closest(CARD_SKIP_ANCESTOR)) return;
        var slide = card.closest('slideshow-slide');
        if (slide && !isCarouselSlideVisible(slide)) return;
        markRevealSkipped(card);
      });
      return;
    }

    var pending = carouselPending && carouselPending.get(carousel);
    if (!pending) pending = [];

    carousel.querySelectorAll(CARD_SELECTOR).forEach(function (card) {
      if (isExcluded(card) || card.closest(CARD_SKIP_ANCESTOR)) return;
      if (card.classList.contains('hog-reveal') || card.classList.contains('is-visible')) return;
      if (card.classList.contains('reveal-text-animated') || card.classList.contains('hog-reveal-done')) return;

      var slide = card.closest('slideshow-slide');
      // Off-screen slides stay natural-visible — never hide; swipe must not animate.
      if (slide && !isCarouselSlideVisible(slide)) {
        markRevealSkipped(card);
        return;
      }

      if (shouldSkipAtfMotion(card)) {
        markRevealSkipped(card);
        return;
      }

      card.classList.add('hog-reveal', 'hog-reveal--card');
      pending.push(card);
    });

    if (!pending.length) return;
    if (carouselPending) carouselPending.set(carousel, pending);

    if (carousel.dataset.hogCarouselRevealObserved === 'true') {
      // Re-arm if late-hydrated cards arrived after the first observe/flush.
      observer.observe(carousel);
      return;
    }
    carousel.dataset.hogCarouselRevealObserved = 'true';
    observer.observe(carousel);
  }

  // Observe for scroll reveal. ATF/near-viewport nodes stay static in prepare().
  function observeOrRevealNow(el) {
    if (!observer || !el) return;
    if (isIoNearViewport(el)) {
      reveal(el);
      return;
    }
    observer.observe(el);
  }

  function scan(root) {
    if (!observer) return;

    if (!headingMotionOwnsHeadings()) {
      root.querySelectorAll(HEADING_SELECTOR).forEach(function (heading) {
        if (prepare(heading, 'heading', 0)) observeOrRevealNow(heading);
      });

      root.querySelectorAll('.hog-split-reveal').forEach(function (block) {
        var target = resolveSplitRevealTarget(block);
        if (!target || target.matches('h1, h2')) return;
        if (prepare(target, 'heading', 0)) observeOrRevealNow(target);
      });
    }

    var carousels = new Set();
    var groups = new Map();

    root.querySelectorAll(CARD_SELECTOR).forEach(function (card) {
      if (isExcluded(card) || card.closest(CARD_SKIP_ANCESTOR)) return;

      var carousel = card.closest('.resource-list__carousel');
      if (carousel) {
        carousels.add(carousel);
        return;
      }

      var container = card.closest('section, .container, .page-width, ul, .grid') || document.body;
      if (!groups.has(container)) groups.set(container, 0);
      var index = groups.get(container);
      if (prepare(card, 'card', Math.min(index, 8))) {
        observeOrRevealNow(card);
        groups.set(container, index + 1);
      }
    });

    carousels.forEach(prepareCarouselGroup);
  }

  function initLiteIOScan() {
    observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          var target = entry.target;
          if (target.classList && target.classList.contains('resource-list__carousel')) {
            flushCarouselReveal(target);
            observer.unobserve(target);
            return;
          }
          reveal(target);
          observer.unobserve(target);
        });
      },
      { threshold: 0.15, rootMargin: '0px 0px -8% 0px' }
    );

    scan(document);

    window.addEventListener(
      'load',
      function () {
        scan(document);
        window.setTimeout(function () {
          scan(document);
        }, 500);
      },
      { once: true }
    );

    var pending = null;
    var mo = new MutationObserver(function (mutations) {
      if (pending) return;
      var hasAdds = mutations.some(function (m) {
        return m.addedNodes && m.addedNodes.length;
      });
      if (!hasAdds) return;
      pending = window.requestAnimationFrame(function () {
        pending = null;
        scan(document);
      });
    });
    mo.observe(document.body, { childList: true, subtree: true });
  }

  function init() {
    if (!shouldRunFadeupMotion()) {
      clearMotionPending();
      return;
    }

    if ('IntersectionObserver' in window) {
      initAllLoadEntrances();
      registerPageScrollSections(document);
      initOurStoryScrollFadeup();
    } else {
      clearMotionPending();
    }

    if (shouldRunLiteIOScan() && 'IntersectionObserver' in window) {
      initLiteIOScan();
    }

    if (!document.querySelector('.media-with-content, .hog-two-column-story, .section-content-wrapper')) {
      clearMotionPending();
    }

    if (window.Shopify && window.Shopify.designMode) {
      document.addEventListener('shopify:section:load', function (event) {
        var target = event.target;
        resetChoreographyFlags(target);
        initAllLoadEntrances(target);
        registerPageScrollSections(target);
        registerOurStoryScrollSections(target);
        if (observer) scan(target);
      });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
